// FILE: src/App.js
import React from "react";
import { Routes, Route } from "react-router-dom";

import Layout from "./layouts/MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";

import AdminDashboard from "./pages/AdminDashboardPage";
import LoginPage from "./pages/LoginPage";

import NotFoundPage from "./pages/NotFoundPage";
import ForbiddenPage from "./pages/ForbiddenPage";
import UserList from "./pages/AdminUsersListPage";
import AddUserForm from "./pages/AddUserFormPage";
import EditUser from "./pages/AdminEditUserPage";
import FeedbackDetail from "./pages/AdminFeedbackDetailPage";
import FeedbackList from "./pages/AdminFeedbackListPage";
import TicketDetail from "./pages/AdminTicketDetailPage";
import TicketList from "./pages/AdminTicketListPage";
import ComplianceUserDetail from "./pages/AdminCompliaceUserDetailPage";
import ComplianceReport from "./pages/AdminComplianceReportPage";
import AuditLog from "./pages/AdminAuditLogPage";
import AdminResourceEdit from "./pages/AdminResourceEditPage";
import AdminResourceList from "./pages/AdminResourceListPage";
import ConfirmInvitePage from "./pages/ConfirmInvitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import StudentResourcesList from "./pages/StudentResourceListPage";
import StudentDashboard from "./pages/StudentDashboardPage";
import StudentResourceView from "./pages/StudentResourceViewPage";
import StudentSupport from "./pages/StudentSupportPage";
import StudentNewTicket from "./pages/StudentNewTicketPage";
import StudentTicketDetail from "./pages/StudentTicketDetailPage";
import StudentProfile from "./pages/StudentProfilePage";
import StudentActivity from "./pages/StudentActivityPage";
import SystemRequirementsPage, { StudentSystemRequirementsPage } from "./pages/SystemRequirementsPage";
import ProfileSecurityPage from "./pages/profile/ProfileSecurityPage";
import ProfileDisplayPage from "./pages/profile/ProfileDisplayPage";
import ProfilePrivacyPage from "./pages/profile/ProfilePrivacyPage";
import { HelpArticlePage, HelpCentrePage } from "./pages/info/HelpPages";
import GlossaryPage from "./pages/info/GlossaryPage";
import ContactPage from "./pages/info/ContactPage";
import AccommodationPage from "./pages/info/AccommodationPage";
import LegalDocumentPage from "./pages/info/LegalDocumentPage";
import AccessibilityPage from "./pages/info/AccessibilityPage";
import OnboardingPage from "./pages/OnboardingPage";
import StudentSurveyPage from "./pages/StudentSurveyPage";
import CourseInfoPage, { TutorialPage } from "./pages/CourseInfoPage";
import AdminAccommodationsPage from "./pages/admin/AdminAccommodationsPage";
import AdminAccommodationDetailPage from "./pages/admin/AdminAccommodationDetailPage";
import AdminSupportLogPage from "./pages/admin/AdminSupportLogPage";
import AdminSupportReportPage from "./pages/admin/AdminSupportReportPage";
import AdminAccessLinksPage from "./pages/admin/AdminAccessLinksPage";
import AccessLinkPage from "./pages/AccessLinkPage";
import AdminStudentLayout from "./pages/admin/student/AdminStudentLayout";
import AdminStudentOverviewPage from "./pages/admin/student/AdminStudentOverviewPage";
import AdminStudentProgressPage from "./pages/admin/student/AdminStudentProgressPage";
import AdminStudentActivityPage from "./pages/admin/student/AdminStudentActivityPage";
import AdminStudentSupportPage from "./pages/admin/student/AdminStudentSupportPage";
import AdminStudentRecordsPage from "./pages/admin/student/AdminStudentRecordsPage";
import AdminExportsPage from "./pages/admin/AdminExportsPage";
import AdminDailyHoursPage from "./pages/admin/AdminDailyHoursPage";
import AdminQuizResultsPage from "./pages/admin/AdminQuizResultsPage";
import AdminResourceDetailPage from "./pages/admin/AdminResourceDetailPage";
import AdminResourceSlidesPage from "./pages/admin/AdminResourceSlidesPage";
import AdminCurriculaPage, { AdminCurriculumNewPage } from "./pages/admin/AdminCurriculaPage";
import AdminCurriculumDetailPage from "./pages/admin/AdminCurriculumDetailPage";
import AdminCurriculumValidationPage from "./pages/admin/AdminCurriculumValidationPage";
import PermissionGate from "./components/admin/NoAccess";
import AdminStaffListPage from "./pages/admin/staff/AdminStaffListPage";
import AdminStaffInvitePage from "./pages/admin/staff/AdminStaffInvitePage";
import AdminRolesPage from "./pages/admin/staff/AdminRolesPage";
import AdminRoleEditPage from "./pages/admin/staff/AdminRoleEditPage";

// Signed-in page helpers (role check + app shell). Staff pages also name
// the permission they need (RBAC; src/auth/permissions.js): without it the
// page explains that the role doesn't include it. The server checks too.
const student = (el) => (
  <ProtectedRoute roles={["student"]}>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);
const adminOnly = (el, need, options = {}) => (
  <ProtectedRoute roles={["admin"]}>
    <Layout>
      <PermissionGate need={need} {...options}>
        {el}
      </PermissionGate>
    </Layout>
  </ProtectedRoute>
);
// Inside a page that already has its h1 (a student's section pages).
const section = (el, need) => (
  <PermissionGate need={need} headingLevel={2} title="You don't have access to this section">
    {el}
  </PermissionGate>
);
const STUDENTS_OR_STAFF = { anyOf: ["students.view", "staff.manage"] };

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/system-requirements" element={<SystemRequirementsPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confirm-invite" element={<ConfirmInvitePage />} />
      {/* Public information (MTO 12.1, 12.3, 6.6, 18.1; PIPEDA; AODA) */}
      <Route path="/help" element={<HelpCentrePage />} />
      <Route path="/help/:id" element={<HelpArticlePage />} />
      <Route path="/glossary" element={<GlossaryPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/accommodation" element={<AccommodationPage />} />
      <Route path="/privacy" element={<LegalDocumentPage type="privacy" />} />
      <Route path="/code-of-conduct" element={<LegalDocumentPage type="codeOfConduct" />} />
      <Route path="/syllabus" element={<LegalDocumentPage type="syllabus" />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />

      {/* Student: getting started, course information, help, survey (WP-6) */}
      <Route path="/student/getting-started" element={student(<OnboardingPage />)} />
      <Route path="/student/course-info" element={student(<CourseInfoPage />)} />
      <Route path="/student/course-info/tutorial" element={student(<TutorialPage />)} />
      <Route path="/student/course-info/code-of-conduct" element={student(<LegalDocumentPage type="codeOfConduct" variant="student" />)} />
      <Route path="/student/course-info/syllabus" element={student(<LegalDocumentPage type="syllabus" variant="student" />)} />
      <Route path="/student/help" element={student(<HelpCentrePage variant="student" />)} />
      <Route path="/student/help/:id" element={student(<HelpArticlePage variant="student" />)} />
      <Route path="/student/glossary" element={student(<GlossaryPage variant="student" />)} />
      <Route path="/student/accessibility" element={student(<AccessibilityPage variant="student" />)} />
      <Route path="/student/survey" element={student(<StudentSurveyPage />)} />
      <Route path="/admin/accommodations" element={adminOnly(<AdminAccommodationsPage />, "accommodations.manage")} />
      <Route path="/admin/accommodations/:id" element={adminOnly(<AdminAccommodationDetailPage />, "accommodations.manage")} />
      {/* Staff: phone/email requests and the 48-hour service-level report (WP-6, MTO 10.1–10.2) */}
      <Route path="/admin/support/log" element={adminOnly(<AdminSupportLogPage />, "support.log")} />
      <Route path="/admin/support/report" element={adminOnly(<AdminSupportReportPage />, "support.report")} />
      {/* Reviewer access links (WP-2b): admin page and the public link */}
      <Route path="/admin/access-links" element={adminOnly(<AdminAccessLinksPage />, "access_links.manage")} />
      <Route path="/access/:token" element={<AccessLinkPage />} />
      {/* Staff: one account's pages, fetched by id (WP-7, B-21, MTO 11.1, matrix 1.13) */}
      <Route path="/admin/users/:id" element={adminOnly(<AdminStudentLayout />, STUDENTS_OR_STAFF)}>
        <Route index element={<AdminStudentOverviewPage />} />
        <Route path="progress" element={section(<AdminStudentProgressPage />, "students.view")} />
        <Route path="activity" element={section(<AdminStudentActivityPage />, "audit.view")} />
        <Route path="support" element={section(<AdminStudentSupportPage />, "support.view")} />
        <Route path="records" element={section(<AdminStudentRecordsPage />, "records.export")} />
      </Route>
      {/* Staff: records in bulk and daily learning hours (WP-7, MTO 11.4, 11.14) */}
      <Route path="/admin/exports" element={adminOnly(<AdminExportsPage />, "records.export")} />
      <Route path="/admin/exports/daily-hours" element={adminOnly(<AdminDailyHoursPage />, "records.export")} />
      <Route path="/admin/quiz-results" element={adminOnly(<AdminQuizResultsPage />, "dashboard.view")} />

      {/* Protected Route require 'student' role */}
      <Route
        path="/student"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/activity"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentActivity />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student/profile"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentProfile />
            </Layout>
          </ProtectedRoute>
        }
      />
      {["security", "display", "privacy"].map((section) => {
        const Page = { security: ProfileSecurityPage, display: ProfileDisplayPage, privacy: ProfilePrivacyPage }[section];
        return (
          <Route
            key={section}
            path={`/student/profile/${section}`}
            element={
              <ProtectedRoute roles={["student"]}>
                <Layout>
                  <Page />
                </Layout>
              </ProtectedRoute>
            }
          />
        );
      })}
      <Route
        path="/student/system-requirements"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentSystemRequirementsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/support"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentSupport />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/support/new"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentNewTicket />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/support/tickets/:id"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentTicketDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/resources/:resourceId"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentResourceView />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/resources/chapters"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentResourcesList type="CHAPTER" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/resources/homelinks"
        element={
          <ProtectedRoute roles={["student"]}>
            <Layout>
              <StudentResourcesList type="HOMELINK" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Protected Route requiring 'admin' role */}
      <Route path="/admin" element={adminOnly(<AdminDashboard />, "dashboard.view", { redirect: true })} />

      <Route path="/admin/users" element={adminOnly(<UserList />, "students.view")} />
      <Route path="/admin/users/add" element={adminOnly(<AddUserForm />, "students.invite")} />
      <Route path="/admin/users/:id/edit" element={adminOnly(<EditUser />, { anyOf: ["students.edit", "staff.manage"] })} />

      <Route path="/admin/resources" element={adminOnly(<AdminResourceList />, "content.view")} />
      <Route path="/admin/resources/new" element={adminOnly(<AdminResourceEdit mode="create" />, "content.edit")} />
      <Route path="/admin/resources/:id" element={adminOnly(<AdminResourceDetailPage />, "content.view")} />
      <Route path="/admin/resources/:id/edit" element={adminOnly(<AdminResourceEdit />, "content.edit")} />
      <Route path="/admin/resources/:id/slides" element={adminOnly(<AdminResourceSlidesPage />, "content.edit")} />
      <Route path="/admin/curricula" element={adminOnly(<AdminCurriculaPage />, "content.view")} />
      <Route path="/admin/curricula/new" element={adminOnly(<AdminCurriculumNewPage />, "curricula.manage")} />
      <Route path="/admin/curricula/:id" element={adminOnly(<AdminCurriculumDetailPage />, "content.view")} />
      <Route path="/admin/curricula/:id/validation" element={adminOnly(<AdminCurriculumValidationPage />, "curricula.manage")} />

      <Route path="/admin/audit-logs" element={adminOnly(<AuditLog />, "audit.view")} />

      <Route path="/admin/compliance" element={adminOnly(<ComplianceReport />, "records.view")} />
      <Route path="/admin/compliance/:userId" element={adminOnly(<ComplianceUserDetail />, "records.view")} />

      <Route path="/admin/support" element={adminOnly(<TicketList />, "support.view")} />
      <Route path="/admin/support/:ticketId" element={adminOnly(<TicketDetail />, "support.view")} />

      <Route path="/admin/feedback" element={adminOnly(<FeedbackList />, "survey.view")} />
      <Route path="/admin/feedback/:id" element={adminOnly(<FeedbackDetail />, "survey.view")} />

      {/* Staff and roles (RBAC) */}
      <Route path="/admin/staff" element={adminOnly(<AdminStaffListPage />, "staff.manage")} />
      <Route path="/admin/staff/invite" element={adminOnly(<AdminStaffInvitePage />, "staff.manage")} />
      <Route path="/admin/roles" element={adminOnly(<AdminRolesPage />, { anyOf: ["roles.manage", "staff.manage"] })} />
      <Route path="/admin/roles/new" element={adminOnly(<AdminRoleEditPage mode="create" />, "roles.manage")} />
      <Route path="/admin/roles/:id" element={adminOnly(<AdminRoleEditPage />, { anyOf: ["roles.manage", "staff.manage"] })} />

      {/* 403 Page */}
      <Route path="/403" element={<ForbiddenPage />} />

      {/* 404 Catch-All Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
